export type CompareOptions = {
  ignoreWhitespace?: boolean;
  ignoreCase?: boolean;
  trimTrailingWhitespace?: boolean;
};

export type DiffRowType = 'equal' | 'insert' | 'delete' | 'replace';

export type DiffRow = {
  type: DiffRowType;
  leftLineNumber: number | null;
  rightLineNumber: number | null;
  leftText: string;
  rightText: string;
};

type LineToken = {
  raw: string;
  norm: string;
};

type DiffOp<T> =
  | { type: 'equal'; left: T; right: T }
  | { type: 'delete'; left: T }
  | { type: 'insert'; right: T };

const DEFAULT_OPTIONS: Required<CompareOptions> = {
  ignoreWhitespace: false,
  ignoreCase: false,
  trimTrailingWhitespace: true,
};

export function splitLines(text: string): string[] {
  if (text.length === 0) return [];

  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');

  if (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }

  return lines;
}

function normalizeLine(raw: string, options: Required<CompareOptions>): string {
  let value = raw;

  if (options.trimTrailingWhitespace) {
    value = value.replace(/[ \t]+$/g, '');
  }

  if (options.ignoreWhitespace) {
    value = value.replace(/\s+/g, '');
  }

  if (options.ignoreCase) {
    value = value.toLowerCase();
  }

  return value;
}

function myersDiff<T>(
  left: readonly T[],
  right: readonly T[],
  equals: (a: T, b: T) => boolean
): DiffOp<T>[] {
  const n = left.length;
  const m = right.length;
  const max = n + m;
  const offset = max;

  let v = new Array<number>(2 * max + 1).fill(-1);
  v[offset + 1] = 0;

  const trace: number[][] = [];

  for (let d = 0; d <= max; d++) {
    const vNext = v.slice();

    for (let k = -d; k <= d; k += 2) {
      const kIndex = offset + k;

      let x: number;
      if (
        k === -d ||
        (k !== d && v[kIndex - 1] < v[kIndex + 1])
      ) {
        x = v[kIndex + 1];
      } else {
        x = v[kIndex - 1] + 1;
      }

      let y = x - k;
      while (x < n && y < m && equals(left[x], right[y])) {
        x++;
        y++;
      }

      vNext[kIndex] = x;

      if (x >= n && y >= m) {
        trace.push(vNext);
        return backtrack(trace, left, right, equals, offset);
      }
    }

    trace.push(vNext);
    v = vNext;
  }

  return [];
}

function backtrack<T>(
  trace: number[][],
  left: readonly T[],
  right: readonly T[],
  equals: (a: T, b: T) => boolean,
  offset: number
): DiffOp<T>[] {
  let x = left.length;
  let y = right.length;

  const ops: DiffOp<T>[] = [];

  for (let d = trace.length - 1; d > 0; d--) {
    const vPrev = trace[d - 1];

    const k = x - y;

    let prevK: number;
    if (
      k === -d ||
      (k !== d && vPrev[offset + k - 1] < vPrev[offset + k + 1])
    ) {
      prevK = k + 1; // insertion
    } else {
      prevK = k - 1; // deletion
    }

    const prevX = vPrev[offset + prevK];
    const prevY = prevX - prevK;

    const isInsert = prevK === k + 1;
    const xStart = isInsert ? prevX : prevX + 1;
    const yStart = isInsert ? prevY + 1 : prevY;

    while (x > xStart && y > yStart) {
      ops.push({ type: 'equal', left: left[x - 1], right: right[y - 1] });
      x--;
      y--;
    }

    if (isInsert) {
      ops.push({ type: 'insert', right: right[yStart - 1] });
    } else {
      ops.push({ type: 'delete', left: left[xStart - 1] });
    }

    x = prevX;
    y = prevY;
  }

  while (x > 0 && y > 0 && equals(left[x - 1], right[y - 1])) {
    ops.push({ type: 'equal', left: left[x - 1], right: right[y - 1] });
    x--;
    y--;
  }

  ops.reverse();
  return ops;
}

export function compareCode(
  leftText: string,
  rightText: string,
  options: CompareOptions = {}
): DiffRow[] {
  const resolvedOptions: Required<CompareOptions> = { ...DEFAULT_OPTIONS, ...options };

  const leftLines = splitLines(leftText);
  const rightLines = splitLines(rightText);

  const leftTokens: LineToken[] = leftLines.map((raw) => ({
    raw,
    norm: normalizeLine(raw, resolvedOptions),
  }));
  const rightTokens: LineToken[] = rightLines.map((raw) => ({
    raw,
    norm: normalizeLine(raw, resolvedOptions),
  }));

  const ops = myersDiff(leftTokens, rightTokens, (a, b) => a.norm === b.norm);

  const rows: DiffRow[] = [];
  let leftLineNumber = 1;
  let rightLineNumber = 1;

  let index = 0;
  while (index < ops.length) {
    const op = ops[index];

    if (op.type === 'equal') {
      rows.push({
        type: 'equal',
        leftLineNumber,
        rightLineNumber,
        leftText: op.left.raw,
        rightText: op.right.raw,
      });
      leftLineNumber++;
      rightLineNumber++;
      index++;
      continue;
    }

    const deletes: LineToken[] = [];
    const inserts: LineToken[] = [];

    while (index < ops.length && ops[index].type !== 'equal') {
      const current = ops[index];
      if (current.type === 'delete') deletes.push(current.left);
      if (current.type === 'insert') inserts.push(current.right);
      index++;
    }

    const pairCount = Math.max(deletes.length, inserts.length);
    for (let i = 0; i < pairCount; i++) {
      const del = deletes[i];
      const ins = inserts[i];

      if (del && ins) {
        rows.push({
          type: 'replace',
          leftLineNumber,
          rightLineNumber,
          leftText: del.raw,
          rightText: ins.raw,
        });
        leftLineNumber++;
        rightLineNumber++;
        continue;
      }

      if (del) {
        rows.push({
          type: 'delete',
          leftLineNumber,
          rightLineNumber: null,
          leftText: del.raw,
          rightText: '',
        });
        leftLineNumber++;
        continue;
      }

      if (ins) {
        rows.push({
          type: 'insert',
          leftLineNumber: null,
          rightLineNumber,
          leftText: '',
          rightText: ins.raw,
        });
        rightLineNumber++;
      }
    }
  }

  return rows;
}
